import type {
  IArticle2025Schema
} from "~/types/models/Article2025.model";
import type { IArticleSchema } from "~/types/models/Article.model";
import type { IAuthorSchema } from "~/types/models/Author.model";
import type { IAffiliationsSchema } from "~/types/models/Affiliations.model";

/**
 * Maps a 2025 affiliations schema to the legacy affiliations schema format
 */
function mapAffiliations2025ToLegacy(
  affiliation2025: IAffiliationsSchema
): IAffiliationsSchema {
  return {
    AddressLine1: null,
    AddressLine2: null,
    AddressLine3: null,
    City: affiliation2025.City,
    Country: affiliation2025.Country,
    Department: affiliation2025.Department,
    Organization: affiliation2025.Organization,
    Position: null,
    PostalCode: affiliation2025.PostalCode,
    State: affiliation2025.State
  };
}

/**
 * Maps a 2025 author schema to the legacy author schema format
 */
type ArticleAuthor2025 = IAuthorSchema & {
  Affiliation?: IAffiliationsSchema;
};

export function mapArticleAuthor2025ToLegacy(
  author2025: ArticleAuthor2025
): IAuthorSchema {
  const nameFromParts = [
    author2025.FirstName,
    author2025.MiddleName,
    author2025.LastName
  ]
    .filter(Boolean)
    .join(" ");

  const affiliations = author2025.Affiliations?.length
    ? author2025.Affiliations.map(mapAffiliations2025ToLegacy)
    : author2025.Affiliation
      ? [mapAffiliations2025ToLegacy(author2025.Affiliation)]
      : [];

  return {
    UserId: author2025.UserId,
    FullName: author2025.FullName || nameFromParts,
    Email: author2025.Email,
    FirstName: author2025.FirstName,
    MiddleName: author2025.MiddleName,
    LastName: author2025.LastName,
    Affiliations: affiliations
  };
}

/**
 * Maps a 2025 article schema to the legacy schema format
 */
export function mapArticle2025ToLegacy(
  article2025: IArticle2025Schema
): IArticleSchema {
  return {
    ArticleId: article2025.ArticleId,
    DOI: article2025.DOI,
    Title: article2025.Title,
    AcceptedDate: String(article2025.AcceptedDate),
    ReceivedDate: String(article2025.ReceivedDate),
    PublishedDate: article2025.PublishedDate,
    RecentDate: article2025.PublishedDate,
    ArticleType: article2025.ArticleType,
    Published: article2025.Published,
    Authors: (article2025.Authors || []).map(mapArticleAuthor2025ToLegacy) as [IAuthorSchema],
    ImpactV5: {
      Citations: article2025.ImpactV5?.Citations || 0,
      Downloads: article2025.ImpactV5?.Downloads || 0,
      Views: article2025.ImpactV5?.Views || 0,
      ViewsAndDownloads: article2025.ImpactV5?.ViewsAndDownloads || 0
    },
    Impact: {
      Citations: 0,
      Downloads: 0,
      Views: 0,
      ViewsAndDownloads: 0
    },
    Journal: article2025.Journal
      ? { JournalId: article2025.Journal.JournalId, JournalSlug: article2025.Journal.JournalSlug }
      : undefined,
    Section: article2025.Section
      ? { SectionId: article2025.Section.SectionId }
      : undefined,
    Score: article2025.Score ?? 0,
    Abstract: article2025.Abstract,
    BodyText: article2025.BodyText,
    FieldCode: article2025.FieldCode,
    Image: article2025.Image,
    ImageUrl: article2025.Image?.Url
  };
}
